<template>
  <responsive-drawer-layout>
    <template v-slot:drawer>
      <v-tabs v-model="tab" grow>
        <v-tab>{{ $t('module.wagenbuch.eingabe_assistent') }}</v-tab>
        <v-tab>{{ $t('menu.module.unfalldesk.document') }}</v-tab>
      </v-tabs>
      <v-list dense>
        <v-list-item-group v-model="activeNavItem" mandatory>
          <v-tabs-items v-model="tab">
            <v-tab-item>
              <v-list-item
                v-for="(item, index) in navigationItems"
                :key="String(index)"
                :value="String(index)"
                :style="item.value === 'none' && { display: 'none' }"
                @click="onNextClick(index)"
              >
                <v-list-item-icon>
                  <v-icon :color="item.value">
                    {{ item.value === 'green' ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle' }}
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.key }}</v-list-item-title>
              </v-list-item>
            </v-tab-item>
            <v-tab-item>
              <v-list-item v-for="doc in udDocuments" :key="'ud-doc-' + doc.filename" @click="openDocument(doc)">
                <v-list-item-icon>
                  <v-icon>mdi-file-document-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ doc.title }}</v-list-item-title>
              </v-list-item>
              <div class="pa-6" v-if="udDocuments.length === 0">{{ $t('noDocuments') }}</div>
            </v-tab-item>
          </v-tabs-items>
        </v-list-item-group>
      </v-list>
      <!--<pre>{{ formInput }}</pre>-->
    </template>

    <template v-slot:content>
      <editor-factory
        v-if="dataLoaded"
        namespace="unfalldesk"
        :title="$t(activeConfig[active.index].title)"
        :cells="activeConfig[active.index].cells"
        :multiplierCell="activeConfig[active.index].multiplierCell"
        :multiplierCount="multiplierCount + 1"
        @removeMultiplier="removeMultiplier"
      >
        <template v-slot:card-actions>
          <div class="card-actions">
            <v-btn v-if="active.index !== 0" class="mt-3 ml-3" @click="active.index--">
              {{ $t('back') }}
            </v-btn>
            <v-btn
              v-if="activeConfig[active.index] && activeConfig[active.index].multiplierCell && activeConfig[active.index].multiplierCell === true"
              class="mt-3"
              @click="addMultipleCell"
              color="secondary"
            >
              {{ $t(activeConfig[active.index].multiplierTitle) }}
            </v-btn>
            <span v-else />
            <!-- Placeholder to make 'justify-content: space-between' work -->
            <v-btn v-if="!isLastPage" :disabled="pageNotFilledIn" class="mt-3 mr-3" @click="onNextClick()">
              {{ $t('next') }}
            </v-btn>
            <v-btn v-else color="error" style="color: white" class="mt-2" :disabled="navigationItems.some((n) => n.value === 'orange')" @click="sendForm()">
              {{ $t('send') }}
            </v-btn>
          </div>
        </template>
      </editor-factory>
      <v-skeleton-loader v-else type="card, article, actions" />
      <v-dialog v-if="pdfJsDocumentUrl" :value="true" fullscreen @click:outside="pdfJsDocumentUrl = null">
        <v-card class="pa-1">
          <v-card-title class="pa-0">
            <v-toolbar flat :class="`${theme.appBar.other.color}--text`" :color="theme.appBar.other.bg"
              >{{ displayedDocument.title }}
              <v-spacer></v-spacer>
              <v-btn icon :color="theme.appBar.other.color" @click="pdfJsDocumentUrl = null">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-toolbar>
          </v-card-title>
          <v-card-text class="pa-0" style="height: 100vh">
            <pdf-js :url="pdfJsDocumentUrl" :download-file-name="pdfDownloadName" :hide-pdf-open-icon="hidePdfOpenIcon"></pdf-js>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapState } from 'vuex';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';
import common from '@/mixins/common';
import configMixin from '../../mixins/configMixin';
import EditorFactory from '../wagenbuch/wb-config/WBUDEditorFactory.vue';
import { cellIsMandatory, conditionFilter } from '../wagenbuch/wb-config/wb-common';
import PdfJs from '@/components/pdf/PdfJs';

export default {
  name: 'Unfalldesk',
  mixins: [common, configMixin],
  components: {
    ResponsiveDrawerLayout,
    EditorFactory,
    PdfJs,
  },
  data() {
    return {
      displayedDocument: null,
      navigationItems: [],
      tab: 0,
      activeNavItem: '0',
      pdfJsDocumentUrl: undefined,
      pdfDownloadName: null,
    };
  },
  created() {
    this.$store.commit('unfalldesk/initStore');
    this.init();
  },
  computed: {
    ...mapState('unfalldesk', ['udConfig', 'dataLoaded', 'tray', 'udDocuments']),
    active: {
      get() {
        return this.$store.state.unfalldesk.active;
      },
      set(value) {
        this.$store.state.unfalldesk.active = value;
      },
    },
    trayDate: {
      get() {
        return this.$store.state.unfalldesk.trayDate;
      },
      set(value) {
        this.$store.state.unfalldesk.trayDate = value;
      },
    },
    trayUuid: {
      get() {
        return this.$store.state.unfalldesk.trayUuid;
      },
      set(value) {
        this.$store.state.unfalldesk.trayUuid = value;
      },
    },
    formInput: {
      get() {
        return this.$store.state.unfalldesk.formInput;
      },
      set(value) {
        this.$store.state.unfalldesk.formInput = value;
      },
    },
    multiplierCount: {
      get() {
        return this.formInput[this.activeConfig[this.active.index].multiplierCountKey];
      },
      set(value) {
        this.$set(this.formInput, this.activeConfig[this.active.index].multiplierCountKey, value);
      },
    },
    activeConfigName() {
      return this.active.name;
    },
    activeConfigIndex() {
      return this.active.index;
    },
    activeConfig() {
      let activeConfig = this.$store.state.unfalldesk[this.active.name].filter((nav) => !nav.hideOnWebApp && conditionFilter(this.formInput, nav));

      activeConfig.forEach((c) => {
        c.cells = c.cells.filter((cell) => !cell.hideOnWebApp && conditionFilter(this.formInput, cell));
      });

      return activeConfig;
    },
    isLastPage() {
      //Send is only available if is last page and current item is not pre-configuration
      return this.active.name !== 'udConfig' && this.active.index === this.navigationItems.length - 1;
    },
    pageNotFilledIn() {
      return this.navigationItems[this.active.index].value === 'orange';
    },
    hidePdfOpenIcon() {
      let moduleConfig = this.getModuleConfig('Unfalldesk', this.$route.params.instance);
      if (moduleConfig) {
        return moduleConfig.hidePdfOpenIcon !== undefined ? moduleConfig.hidePdfOpenIcon : false;
      }
      return false;
    },
  },
  methods: {
    init() {
      if (this.dataLoaded) {
        this.navigationItems = this.activeConfig.map((nav) => ({
          key: this.$t(nav.navigationTitle),
          value: !nav.hideNavigationEntry ? 'orange' : 'none',
        }));
        this.checkMandatoryFields();
        this.activeNavItem = String(this.active.index);
      }
    },
    openDocument(doc) {
      this.pdfJsDocumentUrl = this.constructPdfJsFileUrl('wagenbuch', doc);
      this.displayedDocument = doc;

      const selectedDoc = this.udDocuments.find((item) => item.filename === doc.filename);
      if (!selectedDoc) return '';

      const { filename, title } = selectedDoc;

      const extension = filename.split('.')[1];

      if (!extension) return;

      this.pdfDownloadName = `${title}.${extension}`;
    },
    addMultipleCell() {
      this.multiplierCount += 1;
    },
    removeMultiplier() {
      this.activeConfig[this.active.index].cells.forEach((cell) => {
        //Remove properties from form
        this.$delete(this.formInput, cell.key + this.multiplierCount);
      });
      this.multiplierCount -= 1;
    },
    onNextClick(index) {
      this.mx_isLeftDrawerOpen = false;
      if (index !== undefined) {
        this.active.index = index;
        return;
      }
      if (this.active.index !== this.navigationItems.length - 1) {
        this.checkMandatoryFields();
        if (this.navigationItems[this.active.index].value === 'green') {
          //Only go to next page if filled in correctly
          this.active.index += 1;
          this.checkMandatoryFields();
          document.getElementById('editor-factory').scrollTop = 0; //Scroll to top of view on change
        }
      } else {
        //Is last page of udConfig
        this.active = { name: 'udProtokoll', index: 0 };
        this.formInput.type = 'Protokoll'; //Set type used to identify method in api
        this.init(); //Recalculate navigation items
        document.getElementById('editor-factory').scrollTop = 0; //Scroll to top of view on change
      }
    },
    checkMandatoryFields(index = this.active.index) {
      //Check mandatory fields of active page
      let color = 'green';
      const config = this.activeConfig[index];

      let mandatoryFields = config.cells.filter(
        (cell) => cellIsMandatory(this.formInput, cell, config.multiplierCell) && !cell.hideOnWebApp && conditionFilter(this.formInput, cell)
      );

      const numCopies = config.multiplierCell ? this.multiplierCount + 1 : 1; // check all copies

      for (let copyIndex = 0; copyIndex < numCopies; copyIndex++) {
        for (let i = 0; i < mandatoryFields.length; i++) {
          const mandatoryCell = mandatoryFields[i];
          // Declare a single key
          const cellKey = config.multiplierCell ? mandatoryCell.key + copyIndex : mandatoryCell.key;
          if (mandatoryCell && (mandatoryCell.cellName === 'UDRadioCell' || mandatoryCell.cellName === 'WBUDRadioCell')) {
            //For UDRadioCell and WBUDRadioCell at least one must be filled in
            let filledIn = false;
            if (mandatoryCell && mandatoryCell.radios) {
              let radios = mandatoryCell && mandatoryCell.radios ? mandatoryCell.radios : {};
              for (let j = 0; j < Object.keys(radios).length; j++) {
                const key = config.multiplierCell ? Object.keys(radios)[j] + copyIndex : Object.keys(radios)[j];
                if (this.formInput[key]) {
                  filledIn = true;
                  break;
                }
              }
            }
            if (!filledIn && mandatoryCell.radiosSorted) {
              let radiosSorted = mandatoryCell && mandatoryCell.radiosSorted ? mandatoryCell.radiosSorted : [];
              for (let radioSorted of radiosSorted) {
                const key = config.multiplierCell ? radioSorted.key + copyIndex : radioSorted.key;
                if (this.formInput[key]) {
                  filledIn = true;
                  break;
                }
              }
            }
            if (!filledIn) {
              color = 'orange';
              break;
            }
          } else if (
            mandatoryCell.cellName === 'WBUDPaintCell' ||
            mandatoryCell.cellName === 'WBUDBigPaintCell' ||
            mandatoryCell.cellName === 'UDFremdFahrzeugPaintCell'
          ) {
            if (!this.formInput[cellKey] || this.formInput[cellKey].length === 0) {
              color = 'orange';
              break;
            }
          } else if (mandatoryCell.cellName === 'UDSwitchTextFieldCell') {
            if (this.formInput[cellKey] && !this.formInput[mandatoryCell.keyTextField + copyIndex]) {
              color = 'orange';
              break;
            }
          } else if (mandatoryCell.cellName === 'WBUDSwitchCell') {
            if (!this.formInput[cellKey]) {
              color = 'orange';
              break;
            }
          } else {
            if (this.formInput[cellKey] == undefined || this.formInput[cellKey] === '') {
              color = 'orange';
              break;
            }
          }
        }
        if (color === 'orange') break;
      }
      this.navigationItems[index].value = color;
    },
    async sendForm() {
      const moduleConfig = this.getModuleConfig('Unfalldesk', this.$route.params.instance);
      const uuid = this.trayUuid ? this.trayUuid : crypto.randomUUID();
      await this.$store.dispatch('unfalldesk/sendForm', {
        instance: this.$route.params.instance,
        module: moduleConfig.title,
        formData: { ...this.formInput, uuid },
      });
      this.active = { name: 'udConfig', index: 0 };
      this.formInput = {};

      // Remove or Edit Tray-Item

      const tray = this.tray;

      if (moduleConfig.keepInSendboxForDays) {
        tray.forEach((trayItem) => {
          if (trayItem.created === this.trayDate) {
            if (!trayItem.sent) {
              trayItem.sent = new Date().toISOString();
              trayItem.data.uuid = uuid;
            }
          }
        });
        this.$store.commit('unfalldesk/setTray', tray);
      } else {
        const found = tray.find((i) => i.created === this.trayDate);
        if (found !== undefined) {
          const index = tray.indexOf(found);
          tray.splice(index, 1);
          this.$store.commit('unfalldesk/setTray', tray);
        }
      }

      this.trayDate = undefined;
      this.trayUuid = undefined;
      this.$router.push('/');
    },
    saveTray(input) {
      //Save to tray if is past udConfig and change has happened
      if (this.trayDate !== undefined) {
        const tray = this.tray;
        const currentTray = tray.find((t) => t.created === this.trayDate);
        if (currentTray !== undefined) {
          //Update tray entry
          currentTray.data = input;
        } else {
          //Create tray entry
          tray.push({
            data: input,
            created: this.trayDate,
            active: this.active,
          });
        }
        this.$store.commit('unfalldesk/setTray', tray);
      }
    },
  },
  watch: {
    formInput: {
      deep: true,
      handler(input) {
        if (this.dataLoaded) {
          this.checkMandatoryFields();
          this.saveTray(input);
        }
      },
    },
    dataLoaded(val) {
      if (val) {
        this.init();

        //Tray has been loaded - set previous pages to green
        for (let i = 0; i < this.active.index; i++) {
          this.checkMandatoryFields(i);
        }
      }
    },
    active: {
      handler(newValue, oldValue) {
        if (newValue.index !== undefined) {
          this.activeNavItem = String(newValue.index);
        }
        if (newValue?.name != oldValue?.name) {
          this.init();
        }
      },
      deep: true,
      immediate: true,
    },
    activeConfigName(cName) {
      if (cName !== 'udConfig' && this.trayDate === undefined) {
        //Save to tray if is past udConfig and currently no tray item is active
        this.trayDate = new Date().toISOString();
      }
    },
    activeConfigIndex(index) {
      if (this.activeConfig[index].multiplierCell === true) {
        if (this.multiplierCount === undefined) {
          this.multiplierCount = 0;
        }
      }
    },
  },
};
</script>

<style scoped>
.card-actions {
  display: flex;
  justify-content: space-between;
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
}
</style>
